import { useEffect, useMemo } from 'react';
import centroid from '@turf/centroid';
import { useGlMap } from '../../contexts/context';
import {
  MIN_MAP_EDITABLE_ZOOM_LEVEL,
  MIN_MAP_POI_SELECTED_ZOOM_LEVEL,
} from '../../../common/utils/constants/constants';
import { performInterruptionSafeAnimation } from '../helpers/animationUtils';
import { isPOIItem } from '../../../common/utils/helpers/item.ts';

export const itemFocusAnimationOptions = {
  animate: true,
  duration: 2000,
};

export function useItemFocus(item, animationRunner) {
  const glMap = useGlMap();

  const minMapSelectedItemZoomLevel = useMemo(() => {
    return isPOIItem(item)
      ? MIN_MAP_POI_SELECTED_ZOOM_LEVEL
      : MIN_MAP_EDITABLE_ZOOM_LEVEL;
  }, [item]);

  useEffect(() => {
    if (!item || !glMap) {
      return;
    }

    const runAnimation = (animationDuration) =>
      performInterruptionSafeAnimation(
        glMap,
        item.bbox
          ? (duration) => {
              glMap.fitBounds(item.bbox, {
                ...itemFocusAnimationOptions,
                duration,
              });
            }
          : (duration) =>
              glMap.easeTo({
                center: centroid(item.geometry).geometry
                  .coordinates as mapboxgl.LngLatLike,
                zoom: Math.max(minMapSelectedItemZoomLevel, glMap.getZoom()),
                ...itemFocusAnimationOptions,
                duration,
              }),
        animationDuration,
      );

    if (animationRunner) {
      return animationRunner(runAnimation, {
        animationDuration: itemFocusAnimationOptions.duration,
      });
    } else {
      return runAnimation(itemFocusAnimationOptions.duration);
    }
  }, [glMap, item, animationRunner, minMapSelectedItemZoomLevel]);
}
